// @flow
import { createSelector } from 'reselect';
import _ from 'lodash';

import type { Entity } from './index';
import type { GlobalState } from '../../constants';

export type Find = (state: GlobalState, id: Id) => Entity;
type Id = string | number;

const passId = (_state, id) => id;
const makeGetEntityStateBranch = (entity_path: string) => (state: GlobalState) => {
  const entity_state = _.get(state, entity_path);
  if (!entity_state) throw new Error(`Invalid entity_path: "${entity_path}" can not be found in state.`);
  if (!entity_state.by_id) throw new Error(`Invalid entity_path: "${entity_path}" has no "by_id" key.`);

  return entity_state;
};

type FindEntityById = typeof findEntityByIdDefault;
const findEntityByIdDefault = (entity_state: Object, identifier: Id) => {
  if (Array.isArray(identifier)){ // return an array of entities if identifier is an array
    return _.compact(identifier.map(id => entity_state.by_id[id]));
  } else { // otherwise, return a single element
    return entity_state.by_id[identifier];
  }
};

// There are three layers of functions here.
// The outer one, "makeFind" is called once, which allows us to pass in custom findById logic.
// It is currently local to this module, exported for testing, but may be useful for generalization in the future. It should generally only be called on app start.
export const makeFind = (findEntityById: FindEntityById = findEntityByIdDefault) => {
  // The middle one, find, is a factory for a findEntity. It is generally called while composing queries.
  // ex: query(find('product'))
  const find = (entity_path: string) => {
    const getEntityStateBranch = makeGetEntityStateBranch(entity_path);

    // The inner one, "finder, returns actual entities.
    // It is generally called when query is called, but is also called when using the shortcut find(product)(state, id) syntax.
    const findEntity = createSelector([getEntityStateBranch, passId], findEntityById);
    return findEntity;
  };
  return find;
};

export default makeFind();
